import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  daysOverdue,
  formatCents,
  formatDate,
  INVOICE_PAYMENT_STATE_LABELS,
  type Invoice,
} from '@fsm/shared';
import { api } from '../lib/api';
import { keys } from '../lib/queries';
import { useAuth } from '../lib/auth';
import { ListFoot, usePagedList } from '../components/PagedList';
import {
  ConfirmDeleteModal,
  SelectCell,
  SelectionBar,
  useSelection,
} from '../components/Selection';
import { PageFooter } from '../components/Layout';
import {
  Badge,
  Card,
  EmptyState,
  FilterTabs,
  PageHeader,
  Spinner,
  SummaryBand,
  Table,
  Td,
  Th,
} from '../components/ui';

type Filter = 'all' | 'outstanding' | 'draft' | 'sent' | 'paid' | 'void';

const FILTERS: Array<{ key: Filter; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'outstanding', label: 'Outstanding' },
  { key: 'draft', label: 'Draft' },
  { key: 'sent', label: 'Sent' },
  { key: 'paid', label: 'Paid' },
  { key: 'void', label: 'Void' },
];

export function InvoicesPage() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<Filter>('all');

  const list = usePagedList<Invoice>({
    queryKey: keys.invoices(filter),
    fetchPage: (cursor, limit) =>
      api.invoices.list({
        ...(filter === 'all'
          ? {}
          : filter === 'outstanding'
            ? { outstandingOnly: true }
            : { status: filter }),
        cursor,
        limit,
      }),
  });
  const invoices = list.items;
  const isLoading = list.isLoading;

  const totalOutstanding = invoices.reduce(
    (sum, i) => sum + Math.max(i.totals?.balanceDueCents ?? 0, 0),
    0,
  );

  /* Ticking rows and deleting them — see components/Selection.tsx. */
  const { can } = useAuth();
  const selection = useSelection(invoices);
  const [confirming, setConfirming] = useState(false);
  const qc = useQueryClient();

  return (
    <>
      <PageHeader
        eyebrow="Billing"
        title="Invoices"
        subtitle={
          totalOutstanding > 0 ? (
            <>
              <span className="font-semibold text-danger">{formatCents(totalOutstanding)}</span>
              {` outstanding across ${invoices.length} invoices`}
            </>
          ) : (
            `${invoices.length} invoices`
          )
        }
        tabs={
          <FilterTabs
            value={filter}
            onChange={setFilter}
            options={FILTERS.map((f) => ({ value: f.key, label: f.label }))}
          />
        }
      />

      <div className="min-h-0 flex-1 overflow-y-auto px-[30px]">
        <div className="mx-auto max-w-[1860px]">
          <Card
            title={FILTERS.find((f) => f.key === filter)?.label ?? 'Invoices'}
            icon="cost"
            count={invoices.length}
            divided
            bodyClassName="p-0"
            band={
              totalOutstanding > 0 ? (
                <SummaryBand
                  tone="owed"
                  label="Outstanding"
                  caption="Across every invoice in this view"
                  amount={formatCents(totalOutstanding)}
                />
              ) : undefined
            }
          >
            {isLoading ? (
              <div className="grid place-items-center py-16">
                <Spinner className="size-5 text-ink-4" />
              </div>
            ) : invoices.length === 0 ? (
              <EmptyState
                title="No invoices here"
                description="Invoices are created from completed jobs."
              />
            ) : (
              <>
                <SelectionBar
                  count={selection.count}
                  one="invoice"
                  many="invoices"
                  onClear={selection.clear}
                  onDelete={() => setConfirming(true)}
                />
                <Table>
                <thead>
                  <tr>
                    {can('admin') && (
                      <Th className="w-[44px]">
                        <SelectCell
                          checked={selection.allVisible}
                          indeterminate={selection.someVisible}
                          onChange={selection.toggleAll}
                          label="Select all invoices"
                        />
                      </Th>
                    )}
                    <Th className="w-[34%]">Invoice</Th>
                    <Th>Issued</Th>
                    <Th>Due</Th>
                    <Th>Total</Th>
                    <Th>Paid</Th>
                    <Th align="right">Balance</Th>
                    <Th align="right">Status</Th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((invoice: Invoice) => {
                    const balance = invoice.totals?.balanceDueCents ?? 0;
                    const overdue = balance > 0 ? daysOverdue(invoice.dueAt) : 0;
                    return (
                      <tr
                        key={invoice.id}
                        onClick={() => navigate(`/invoices/${invoice.id}`)}
                        className="cursor-pointer transition-colors hover:bg-raised"
                      >
                        {can('admin') && (
                          <Td>
                            <SelectCell
                              checked={selection.isSelected(invoice.id)}
                              onChange={() => selection.toggle(invoice.id)}
                              label={`Select invoice ${invoice.number}`}
                            />
                          </Td>
                        )}
                        <Td>
                          <div className="text-[14px] font-semibold text-ink">
                            {invoice.customer?.name}
                          </div>
                          <div className="mt-1 text-[12px] text-ink-4">
                            Invoice #{invoice.number}
                          </div>
                        </Td>
                        <Td className="tnum">
                          {invoice.issuedAt ? formatDate(invoice.issuedAt) : '—'}
                        </Td>
                        <Td className="tnum">
                          {invoice.dueAt ? formatDate(invoice.dueAt) : '—'}
                          {overdue > 0 && (
                            <div className="mt-1 text-[12px] font-semibold text-danger">
                              {overdue} days late
                            </div>
                          )}
                        </Td>
                        <Td className="tnum">{formatCents(invoice.totals?.totalCents ?? 0)}</Td>
                        <Td className="tnum text-ink-3">
                          {formatCents(invoice.totals?.amountPaidCents ?? 0)}
                        </Td>
                        <Td align="right" className="tnum font-semibold">
                          {balance > 0 ? (
                            <span className="text-danger">{formatCents(balance)}</span>
                          ) : (
                            <span className="text-ink-4">—</span>
                          )}
                        </Td>
                        <Td align="right">
                          <PaymentBadge invoice={invoice} />
                        </Td>
                      </tr>
                    );
                  })}
                </tbody>
                </Table>
                <ListFoot list={list} one="invoice" many="invoices" />
              </>
            )}
          </Card>
        </div>

        <PageFooter label={`${list.total.toLocaleString()} invoices`} />
      </div>

      <ConfirmDeleteModal
        open={confirming}
        onClose={() => setConfirming(false)}
        count={selection.count}
        one="invoice"
        many="invoices"
        warning="Payment requests attached to the invoice go with it. An invoice with payments that are not voided cannot be deleted — those are named back to you."
        onConfirm={() => api.invoices.bulkRemove(selection.selected)}
        onDeleted={() => {
          void qc.invalidateQueries({ queryKey: ['invoices'] });
          selection.clear();
        }}
      />
    </>
  );
}

export function PaymentBadge({ invoice }: { invoice: Invoice }) {
  if (invoice.status === 'void') return <Badge tone="slate">Void</Badge>;
  const state = invoice.paymentState ?? 'unpaid';
  const tone = {
    paid: 'emerald',
    partial: 'amber',
    unpaid: 'red',
    overpaid: 'violet',
  }[state] as 'emerald' | 'amber' | 'red' | 'violet';
  return <Badge tone={tone}>{INVOICE_PAYMENT_STATE_LABELS[state]}</Badge>;
}
